import React, {useContext, useEffect, useState} from "react";
import {Select} from "antd";
import {OrganizationServiceContext, VulReportDefinitionServiceContext} from "../../Contexts";
import Organization from "../../domain/Organization";
import VulReportDefinition from "../../domain/VulReportDefinition";
import QueryOptions from "../../sal-ui/QueryOptions";
import {FilterItem, FilterItemProps, SelectEditor} from "../tablefilter/TableFilter";
import {SearchFilter} from "../tablefilter/SearchFilter";
import * as styles from "./VulReportFilter.module.css";

interface OrganizationItemProps extends FilterItemProps {
    organizations?: Organization[];
    loading: boolean;
}

function OrganizationItem(props: OrganizationItemProps) {
    if (props.loading && props.value?.length > 0) {
        return <LoadingFilterValue/>;
    }

    return (
        <SelectEditor
            width={180}
            multiple={true}
            showSearch={true}
            optionFilterProp="label"
            options={props.organizations?.map(organization => ({
                value: organization.id,
                label: organization.name
            }))}
            value={props.value}
            onChange={value => props.onChange(value)}
        />
    );
}

interface ReportDefinitionItemProps extends FilterItemProps {
    definitions?: VulReportDefinition[];
    loading: boolean;
}

function ReportDefinitionItem(props: ReportDefinitionItemProps) {
    if (props.loading && props.value?.length > 0) {
        return <LoadingFilterValue/>;
    }

    const definitionGroups = (props.definitions || []).reduce<Record<string, VulReportDefinition[]>>((groups, definition) => {
        const groupName = definition.organization?.name || "Organization";

        groups[groupName] = groups[groupName] || [];
        groups[groupName].push(definition);

        return groups;
    }, {});

    return (
        <SelectEditor
            width={240}
            popupMatchSelectWidth={320}
            multiple={true}
            showSearch={true}
            optionLabelProp="label"
            filterOption={(inputValue, option) => {
                const normalizedInput = normalizeSearchText(inputValue);
                const normalizedLabel = normalizeSearchText(String(option?.label ?? ""));

                return normalizedLabel.includes(normalizedInput);
            }}
            value={props.value}
            onChange={value => props.onChange(value)}
        >
            {Object.entries(definitionGroups).map(([organizationName, definitions]) => (
                <Select.OptGroup key={organizationName} label={organizationName}>
                    {definitions.map(definition => (
                        <Select.Option key={definition.id} value={definition.id} label={definition.name}>
                            {definition.name}
                        </Select.Option>
                    ))}
                </Select.OptGroup>
            ))}
        </SelectEditor>
    );
}

function LoadingFilterValue() {
    return <span className={styles["loading-filter-value"]}>Loading...</span>;
}

function normalizeSearchText(value?: string) {
    return value?.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "") ?? "";
}

interface VulReportFilterProps {
    className?: string;
    values?: any;
    onChange: (values: any) => void;
    showOrganization: boolean;
    persistentStateIdent?: string;
}

export function createDefaultVulReportFilterValues() {
    return {organizationIds: [], definitionIds: []};
}

function VulReportFilter(props: VulReportFilterProps) {
    const organizationService = useContext(OrganizationServiceContext);
    const vulReportDefinitionService = useContext(VulReportDefinitionServiceContext);
    const [organizations, setOrganizations] = useState<Organization[]>();
    const [definitions, setDefinitions] = useState<VulReportDefinition[]>();
    const [organizationsLoading, setOrganizationsLoading] = useState(props.showOrganization);
    const [definitionsLoading, setDefinitionsLoading] = useState(true);

    useEffect(() => {
        if (!props.showOrganization) {
            setOrganizationsLoading(false);

            return;
        }

        setOrganizationsLoading(true);

        organizationService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(result => setOrganizations(result.data))
            .finally(() => setOrganizationsLoading(false));
    }, [organizationService, props.showOrganization]);

    useEffect(() => {
        setDefinitionsLoading(true);

        vulReportDefinitionService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(result => setDefinitions(result.data))
            .finally(() => setDefinitionsLoading(false));
    }, [vulReportDefinitionService]);

    const items: FilterItem[] = [
        {
            name: "definitionIds",
            key: "definitionIds",
            label: "Report definition",
            render: itemProps => <ReportDefinitionItem {...itemProps} definitions={definitions} loading={definitionsLoading}/>
        }
    ];

    if (props.showOrganization) {
        items.unshift({
            name: "organizationIds",
            key: "organizationIds",
            label: "Organization",
            render: itemProps => <OrganizationItem {...itemProps} organizations={organizations} loading={organizationsLoading}/>
        });
    }

    return (
        <SearchFilter
            items={items}
            className={props.className}
            values={props.values}
            defaultValues={createDefaultVulReportFilterValues()}
            persistentStateIdent={props.persistentStateIdent}
            defaultActiveFilterNames={["organizationIds", "definitionIds"]}
            onChange={props.onChange}
        />
    );
}

export default VulReportFilter;
